import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { AppState } from '@app/store/state/app.state';
import { breakpoints } from '@shared/ui/atoms/MediaQueries';
import {
  DailyFilter,
  DEFAULT_DAILY_FILTER,
  PageView,
  ViewMode,
} from '@features/whereabouts/pages/whereabouts-v2/types';

const STORAGE_KEYS = {
  viewMode: 'whereabouts-v2-view-mode',
  // Only written when the user picks Grid or Calendar. The older
  // 'whereabouts-v2-page-view' key is not read: month view used to overwrite
  // it with Calendar, which left users stuck on Calendar in week view.
  pageView: 'whereabouts-v2-week-page-view',
  sidebarOpen: 'whereabouts-v2-sidebar-open',
  schengenCountsVisible: 'whereabouts-v2-schengen-counts-visible',
} as const;

export interface WhereaboutsViewState {
  viewMode: ViewMode;
  // The user's Grid/Calendar choice for week view. Month view has a single
  // layout and leaves this untouched, so the toggle keeps showing the choice.
  pageView: PageView;
  dailyFilter: DailyFilter;
  sidebarOpen: boolean;
  notesPanelOpen: boolean;
  globalHeaderCollapsed: boolean;
  schengenCountsVisible: boolean;
}

function readEnum<T extends string>(key: string, values: readonly T[], fallback: T): T {
  if (typeof window === 'undefined') return fallback;
  const saved = window.localStorage.getItem(key);
  return values.includes(saved as T) ? (saved as T) : fallback;
}

function readSidebarOpen(): boolean {
  if (typeof window === 'undefined') return true;
  if (window.innerWidth < breakpoints.tablet) return false;
  const saved = window.localStorage.getItem(STORAGE_KEYS.sidebarOpen);
  return saved === null ? true : saved === 'true';
}

function readSchengenCountsVisible(): boolean {
  if (typeof window === 'undefined') return true;
  return window.localStorage.getItem(STORAGE_KEYS.schengenCountsVisible) !== 'false';
}

export const initialWhereaboutsViewState: WhereaboutsViewState = {
  viewMode: readEnum(STORAGE_KEYS.viewMode, Object.values(ViewMode), ViewMode.Week),
  pageView: readEnum(STORAGE_KEYS.pageView, Object.values(PageView), PageView.Grid),
  dailyFilter: DEFAULT_DAILY_FILTER,
  sidebarOpen: readSidebarOpen(),
  notesPanelOpen: false,
  globalHeaderCollapsed: false,
  schengenCountsVisible: readSchengenCountsVisible(),
};

const slice = createSlice({
  name: 'whereaboutsView',
  initialState: initialWhereaboutsViewState,
  reducers: {
    setViewMode: (state, { payload }: PayloadAction<ViewMode>) => {
      window.localStorage.setItem(STORAGE_KEYS.viewMode, payload);
      state.viewMode = payload;
    },
    setPageView: (state, { payload }: PayloadAction<PageView>) => {
      window.localStorage.setItem(STORAGE_KEYS.pageView, payload);
      state.pageView = payload;
      // Month view has no grid layout, so picking Grid there means week grid
      if (payload === PageView.Grid && state.viewMode === ViewMode.Month) {
        state.viewMode = ViewMode.Week;
        window.localStorage.setItem(STORAGE_KEYS.viewMode, state.viewMode);
      }
    },
    setDailyFilter: (state, { payload }: PayloadAction<DailyFilter>) => {
      state.dailyFilter = payload;
    },
    toggleSidebar: (state) => {
      state.sidebarOpen = !state.sidebarOpen;
      window.localStorage.setItem(STORAGE_KEYS.sidebarOpen, String(state.sidebarOpen));
    },
    setNotesPanelOpen: (state, { payload }: PayloadAction<boolean>) => {
      state.notesPanelOpen = payload;
    },
    toggleNotesPanel: (state) => {
      state.notesPanelOpen = !state.notesPanelOpen;
    },
    setGlobalHeaderCollapsed: (state, { payload }: PayloadAction<boolean>) => {
      state.globalHeaderCollapsed = payload;
    },
    toggleSchengenCounts: (state) => {
      state.schengenCountsVisible = !state.schengenCountsVisible;
      window.localStorage.setItem(STORAGE_KEYS.schengenCountsVisible, String(state.schengenCountsVisible));
    },
  },
});

export default slice.reducer;
export const {
  setViewMode,
  setPageView,
  setDailyFilter,
  toggleSidebar,
  setNotesPanelOpen,
  toggleNotesPanel,
  setGlobalHeaderCollapsed,
  toggleSchengenCounts,
} = slice.actions;

export const selectViewMode = (state: AppState) => state.whereaboutsView.viewMode;
export const selectPageView = (state: AppState) => state.whereaboutsView.pageView;
export const selectDailyFilter = (state: AppState) => state.whereaboutsView.dailyFilter;
export const selectSidebarOpen = (state: AppState) => state.whereaboutsView.sidebarOpen;
export const selectNotesPanelOpen = (state: AppState) => state.whereaboutsView.notesPanelOpen;
export const selectGlobalHeaderCollapsed = (state: AppState) => state.whereaboutsView.globalHeaderCollapsed;
export const selectSchengenCountsVisible = (state: AppState) => state.whereaboutsView.schengenCountsVisible;
export const selectIsMonthView = (state: AppState) => state.whereaboutsView.viewMode === ViewMode.Month;
